import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { Settings, Building2, Banknote, Clock, CalendarDays, FileText, Save, Upload, Trash2, ImageIcon, Receipt, Hash, KeyRound, Paperclip, Download, ToggleLeft, Mail } from 'lucide-react';
import { Switch } from '@/components/ui/switch';

const SECTIONS = [
  { id: 'firma', label: 'Firmenprofil', icon: Building2 },
  { id: 'bank', label: 'Bankdaten', icon: Banknote },
  { id: 'rechnungen', label: 'Rechnungen', icon: Receipt },
  { id: 'nummern', label: 'Nummernkreise', icon: Hash },
  { id: 'email', label: 'E-Mail Versand', icon: Mail },
  { id: 'passwort', label: 'Passwort', icon: KeyRound },
];

export default function Einstellungen() {
  const { API, user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [originalSettings, setOriginalSettings] = useState(null);
  const [activeSection, setActiveSection] = useState('firma');
  const [saving, setSaving] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoLoading, setLogoLoading] = useState(false);
  const logoRef = useRef(null);
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    axios.get(`${API}/settings`).then(r => {
      setSettings(r.data);
      setOriginalSettings(r.data);
    }).catch(() => {});
    const img = new window.Image();
    img.onload = () => setLogoUrl(`${API}/settings/logo?t=${Date.now()}`);
    img.onerror = () => setLogoUrl(null);
    img.src = `${API}/settings/logo`;
  }, [API]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Only send changed fields
      const changed = {};
      if (settings && originalSettings) {
        for (const [key, value] of Object.entries(settings)) {
          if (key === '_type' || key === 'tenant_id') continue;
          if (JSON.stringify(value) !== JSON.stringify(originalSettings[key])) {
            changed[key] = value;
          }
        }
      }
      if (Object.keys(changed).length === 0) {
        toast.info('Keine Änderungen zum Speichern');
        setSaving(false);
        return;
      }
      const res = await axios.put(`${API}/settings`, changed);
      setSettings(res.data);
      setOriginalSettings(res.data);
      toast.success('Einstellungen gespeichert');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally { setSaving(false); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Nur Bilddateien (PNG, JPG)'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Logo max. 5 MB'); return; }
    setLogoLoading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API}/settings/logo`, fd);
      setLogoUrl(`${API}/settings/logo?t=${Date.now()}`);
      toast.success('Logo hochgeladen — wird auf allen PDFs angezeigt');
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); }
    finally { setLogoLoading(false); e.target.value = ''; }
  };

  const handleLogoDelete = async () => {
    try {
      await axios.delete(`${API}/settings/logo`);
      setLogoUrl(null);
      toast.success('Logo entfernt');
    } catch { toast.error('Fehler'); }
  };

  const update = (key, value) => setSettings(prev => ({ ...prev, [key]: value }));

  if (!settings) return <div className="text-slate-500 p-8">Laden...</div>;

  const renderField = (label, field, type = 'text', placeholder = '', disabled = false) => (
    <div>
      <Label className="text-slate-400 text-xs uppercase tracking-wider">{label}</Label>
      <Input value={settings[field] || ''} onChange={e => update(field, type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value)}
        type={type} placeholder={placeholder} disabled={!isAdmin || disabled}
        className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 disabled:opacity-50" />
    </div>
  );

  return (
    <div className="animate-fade-in" data-testid="einstellungen-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-white tracking-tight">Einstellungen</h1>
        {isAdmin && (
          <Button onClick={handleSave} disabled={saving} data-testid="save-settings"
            className="bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold rounded-sm shadow-[0_0_15px_rgba(245,158,11,0.3)]">
            <Save className="w-4 h-4 mr-2" />{saving ? 'Speichern...' : 'Speichern'}
          </Button>
        )}
      </div>

      {!isAdmin && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-sm p-3 mb-4 text-sm text-amber-400">
          Nur Administratoren können Einstellungen ändern.
        </div>
      )}

      <div className="flex gap-6">
        {/* Section nav */}
        <div className="w-48 shrink-0 space-y-1">
          {SECTIONS.map(s => (
            <button key={s.id} onClick={() => setActiveSection(s.id)} data-testid={`section-${s.id}`}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-sm text-sm transition-all text-left
                ${activeSection === s.id ? 'bg-amber-500 text-slate-950 font-semibold' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}>
              <s.icon className="w-4 h-4 shrink-0" strokeWidth={1.5} />
              {s.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 bg-slate-900/50 border border-slate-800/60 rounded-sm p-6">

          {activeSection === 'firma' && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-white mb-4">Firmenprofil</h2>

              {/* Logo Upload */}
              <div className="bg-slate-950/50 border border-slate-800/40 rounded-sm p-4">
                <Label className="text-slate-400 text-xs uppercase tracking-wider flex items-center gap-2 mb-3">
                  <ImageIcon className="w-4 h-4" />Firmenlogo
                </Label>
                <div className="flex items-center gap-4">
                  {logoUrl ? (
                    <div className="relative group">
                      <img src={logoUrl} alt="Logo" className="h-16 max-w-[200px] object-contain rounded-sm border border-slate-800" data-testid="logo-preview" />
                      {isAdmin && (
                        <button onClick={handleLogoDelete} data-testid="delete-logo"
                          className="absolute -top-2 -right-2 w-6 h-6 bg-rose-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <Trash2 className="w-3 h-3 text-white" />
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="h-16 w-40 border-2 border-dashed border-slate-700 rounded-sm flex items-center justify-center text-slate-600 text-xs">
                      Kein Logo
                    </div>
                  )}
                  {isAdmin && (
                    <div>
                      <Button variant="secondary" size="sm" onClick={() => logoRef.current?.click()} disabled={logoLoading}
                        data-testid="upload-logo-button"
                        className="bg-slate-800 text-slate-200 border border-slate-700 hover:bg-slate-700 rounded-sm">
                        <Upload className="w-4 h-4 mr-2" />{logoLoading ? 'Laden...' : (logoUrl ? 'Ersetzen' : 'Logo hochladen')}
                      </Button>
                      <input ref={logoRef} type="file" accept="image/png,image/jpeg,image/svg+xml" className="hidden" onChange={handleLogoUpload} />
                      <p className="text-[10px] text-slate-600 mt-1">PNG, JPG oder SVG · Max. 5 MB</p>
                    </div>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-2">Erscheint oben links auf allen Finanzdokumenten (Angebote, Rechnungen etc.)</p>
              </div>

              {renderField("Firmenname", "company_name", "text", "Meine Firma GmbH")}
              <div className="grid grid-cols-2 gap-4">
                {renderField("Adresse", "company_address")}
                <div className="grid grid-cols-2 gap-3">
                  {renderField("PLZ", "company_zip")}
                  {renderField("Stadt", "company_city")}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {renderField("Telefon", "company_phone")}
                {renderField("E-Mail", "company_email", "email")}
              </div>
              {renderField("Website", "company_website", "text", "https://www.firma.de")}
              {renderField("Steuernummer / USt-IdNr.", "tax_id", "text", "DE123456789")}
              <Separator className="bg-slate-800 my-4" />
              <h3 className="text-sm font-semibold text-slate-300">Rechtliche Angaben (Fußzeile)</h3>
              <div className="grid grid-cols-2 gap-4">
                {renderField("USt-IdNr.", "ust_id", "text", "DE123456789")}
                {renderField("Steuernummer", "steuernummer", "text", "12/345/67890")}
              </div>
              <div className="grid grid-cols-2 gap-4">
                {renderField("Amtsgericht", "amtsgericht", "text", "AG Berlin-Charlottenburg")}
                {renderField("Handelsregister (HRB)", "handelsregister", "text", "HRB 12345")}
              </div>
              <p className="text-xs text-slate-500 mt-2">Diese Angaben erscheinen in der Fußzeile jedes generierten PDF-Dokuments.</p>

              <Separator className="bg-slate-800 my-4" />
              <h3 className="text-sm font-semibold text-white">Dokument-Anhänge nach Kundentyp</h3>
              <p className="text-xs text-slate-500 mb-3">Dokumente die automatisch an Angebote/Rechnungen per E-Mail angehängt werden — getrennt nach Kundentyp.</p>
              <AttachmentManager API={API} isAdmin={isAdmin} />
            </div>
          )}

          {activeSection === 'bank' && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-white mb-4">Bankdaten</h2>
              {renderField("Kontoinhaber", "account_holder", "text", "Firma XY GmbH")}
              {renderField("Bank", "bank_name", "text", "Commerzbank")}
              {renderField("IBAN", "iban", "text", "DE89 3704 0044 0532 0130 00")}
              {renderField("BIC", "bic", "text", "COBADEFFXXX")}
              <Separator className="bg-slate-800 my-4" />
              <h3 className="text-sm font-semibold text-slate-300">Skonto / Zahlungsbedingungen</h3>
              <div className="grid grid-cols-2 gap-4">
                {renderField("Skonto (%)", "skonto_percent", "number", "0")}
                {renderField("Skonto-Frist (Tage)", "skonto_days", "number", "0")}
              </div>
              {renderField("Sonstige Zahlungsinfo (Anzahlung etc.)", "anzahlung_text", "text", "z.B. 30% Anzahlung bei Auftragserteilung")}
              <p className="text-xs text-slate-500 mt-2">Bankdaten und Zahlungsinformationen erscheinen im Zahlungsblock auf jedem Dokument.</p>
            </div>
          )}

          {activeSection === 'rechnungen' && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-white mb-4">Rechnungseinstellungen</h2>

              <div className="bg-slate-950/50 border border-slate-800/40 rounded-sm p-4 space-y-4">
                <h3 className="text-sm font-semibold text-slate-300">Fälligkeiten</h3>
                <div className="grid grid-cols-3 gap-4">
                  {renderField("Zahlungsfrist Rechnung (Tage)", "invoice_due_days", "number")}
                  {renderField("Gültigkeit Angebot (Tage)", "offer_due_days", "number")}
                  {renderField("Frist Gutschrift (Tage)", "credit_due_days", "number")}
                </div>
                <p className="text-xs text-slate-500">Wird automatisch als Fälligkeitsdatum berechnet, wenn kein manuelles Datum gesetzt wird.</p>
              </div>

              <div className="bg-slate-950/50 border border-slate-800/40 rounded-sm p-4 space-y-4">
                <h3 className="text-sm font-semibold text-slate-300">Leistungsdatum</h3>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={settings.show_delivery_date || false}
                      onChange={e => update('show_delivery_date', e.target.checked)} disabled={!isAdmin}
                      className="w-4 h-4 rounded-sm border-slate-600 bg-slate-950 text-amber-500 focus:ring-amber-500" />
                    <span className="text-sm text-slate-300">Leistungsdatum auf Rechnungen anzeigen</span>
                  </label>
                </div>
                {renderField("Leistungsdatum-Text", "delivery_date_text", "text", "Leistungsdatum entspricht Rechnungsdatum")}
              </div>

              <div className="bg-slate-950/50 border border-slate-800/40 rounded-sm p-4 space-y-4">
                <h3 className="text-sm font-semibold text-slate-300">Einleitungstexte</h3>
                <div>
                  <Label className="text-slate-400 text-xs uppercase tracking-wider">Angebot — Einleitung</Label>
                  <Textarea value={settings.offer_intro_text || ''} onChange={e => update('offer_intro_text', e.target.value)}
                    disabled={!isAdmin} placeholder="wir erlauben uns Ihnen folgendes Angebot zu unterbreiten:"
                    className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1 disabled:opacity-50" rows={2} />
                </div>
                <div>
                  <Label className="text-slate-400 text-xs uppercase tracking-wider">Rechnung — Einleitung</Label>
                  <Textarea value={settings.invoice_intro_text || ''} onChange={e => update('invoice_intro_text', e.target.value)}
                    disabled={!isAdmin} placeholder="für die erbrachten Leistungen erlauben wir uns wie folgt abzurechnen:"
                    className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1 disabled:opacity-50" rows={2} />
                </div>
                <div>
                  <Label className="text-slate-400 text-xs uppercase tracking-wider">Gutschrift — Einleitung</Label>
                  <Textarea value={settings.credit_intro_text || ''} onChange={e => update('credit_intro_text', e.target.value)}
                    disabled={!isAdmin} placeholder="wir erstatten Ihnen hiermit folgenden Betrag:"
                    className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1 disabled:opacity-50" rows={2} />
                </div>
                <p className="text-xs text-slate-500">Wird im PDF unterhalb der Kundenadresse vor den Positionen eingefügt.</p>
              </div>

              <div className="bg-slate-950/50 border border-slate-800/40 rounded-sm p-4 space-y-4">
                <h3 className="text-sm font-semibold text-slate-300">Dokument-Einstellungen</h3>
                {renderField("Standard-Steuersatz (%)", "default_tax_rate", "number")}
                <div>
                  <Label className="text-slate-400 text-xs uppercase tracking-wider">Fußzeile / Schlusstext</Label>
                  <Textarea value={settings.invoice_footer_text || ''} onChange={e => update('invoice_footer_text', e.target.value)}
                    disabled={!isAdmin} placeholder="Vielen Dank für Ihren Auftrag!"
                    className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1 disabled:opacity-50" />
                </div>
              </div>
            </div>
          )}

          {activeSection === 'nummern' && (
            <NummernkreiseSection API={API} settings={settings} update={update} isAdmin={isAdmin} />
          )}

          {activeSection === 'email' && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-white mb-4">E-Mail Versand</h2>

              <div className="bg-slate-950/50 border border-slate-800/40 rounded-sm p-4 space-y-4">
                <h3 className="text-sm font-semibold text-slate-300">Absender & Antwort-Adresse</h3>
                <div className="bg-slate-900/60 border border-slate-800/30 rounded-sm p-3 space-y-2 text-xs text-slate-400">
                  <p>Alle E-Mails werden über <span className="font-mono text-amber-400">noreply@mont-aro.de</span> versendet.</p>
                  <p>Beim Empfänger erscheint: <span className="text-slate-200">"{settings.email_sender_name || 'Ihr Firmenname'}" &lt;noreply@mont-aro.de&gt;</span></p>
                  <p>Wenn der Empfänger auf "Antworten" klickt, wird seine Antwort an die unten eingetragene <span className="text-slate-200">Antwort-Adresse</span> weitergeleitet.</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {renderField("Absender-Name (wird dem Empfänger angezeigt)", "email_sender_name", "text", "Meine Firma GmbH")}
                  {renderField("Antwort-Adresse (Reply-To)", "email_reply_to", "text", "info@ihre-firma.de")}
                </div>
              </div>

              <div className="bg-slate-950/50 border border-slate-800/40 rounded-sm p-4 space-y-4">
                <h3 className="text-sm font-semibold text-slate-300">E-Mail-Vorlagen</h3>
                <p className="text-xs text-slate-500 mb-2">Platzhalter: <span className="font-mono text-slate-400">{'{doc_type}'} {'{doc_number}'} {'{total}'} {'{company_name}'} {'{due_date}'}</span></p>

                <div className="space-y-3">
                  <div><Label className="text-slate-400 text-xs uppercase tracking-wider">Betreff — Rechnung / Angebot</Label>
                  <Input value={settings.email_invoice_subject || ''} onChange={e => update('email_invoice_subject', e.target.value)}
                    disabled={!isAdmin} placeholder="{doc_type} {doc_number}" className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1 disabled:opacity-50 font-mono text-sm" /></div>

                  <div><Label className="text-slate-400 text-xs uppercase tracking-wider">Text — Rechnung / Angebot</Label>
                  <Textarea value={settings.email_invoice_body || ''} onChange={e => update('email_invoice_body', e.target.value)}
                    disabled={!isAdmin} rows={3} className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1 disabled:opacity-50 text-sm" /></div>
                </div>

                <Separator className="bg-slate-800" />

                <div className="space-y-3">
                  <div><Label className="text-slate-400 text-xs uppercase tracking-wider">Betreff — Zahlungserinnerung</Label>
                  <Input value={settings.email_reminder_subject || ''} onChange={e => update('email_reminder_subject', e.target.value)}
                    disabled={!isAdmin} className="bg-slate-950 border-slate-800 text-slate-200 mt-1 disabled:opacity-50 font-mono text-sm" /></div>

                  <div><Label className="text-slate-400 text-xs uppercase tracking-wider">Text — Zahlungserinnerung</Label>
                  <Textarea value={settings.email_reminder_body || ''} onChange={e => update('email_reminder_body', e.target.value)}
                    disabled={!isAdmin} rows={3} className="bg-slate-950 border-slate-800 text-slate-200 mt-1 disabled:opacity-50 text-sm" /></div>
                </div>

                <Separator className="bg-slate-800" />

                <div className="space-y-3">
                  <div><Label className="text-slate-400 text-xs uppercase tracking-wider">Betreff — Mahnung</Label>
                  <Input value={settings.email_dunning_subject || ''} onChange={e => update('email_dunning_subject', e.target.value)}
                    disabled={!isAdmin} className="bg-slate-950 border-slate-800 text-slate-200 mt-1 disabled:opacity-50 font-mono text-sm" /></div>

                  <div><Label className="text-slate-400 text-xs uppercase tracking-wider">Text — Mahnung</Label>
                  <Textarea value={settings.email_dunning_body || ''} onChange={e => update('email_dunning_body', e.target.value)}
                    disabled={!isAdmin} rows={3} className="bg-slate-950 border-slate-800 text-slate-200 mt-1 disabled:opacity-50 text-sm" /></div>
                </div>

                <Separator className="bg-slate-800" />

                <div><Label className="text-slate-400 text-xs uppercase tracking-wider">E-Mail-Signatur</Label>
                <Textarea value={settings.email_signature || ''} onChange={e => update('email_signature', e.target.value)}
                  disabled={!isAdmin} rows={2} placeholder="Mit freundlichen Grüßen\n{company_name}"
                  className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1 disabled:opacity-50 text-sm" /></div>
              </div>
            </div>
          )}

          {activeSection === 'passwort' && (
            <PasswordSection API={API} />
          )}
        </div>
      </div>
    </div>
  );
}

function PasswordSection({ API }) {
  const [pw, setPw] = useState({ current_password: '', new_password: '', confirm: '' });
  const [saving, setSaving] = useState(false);

  const handleChange = async () => {
    if (pw.new_password !== pw.confirm) { toast.error('Passwörter stimmen nicht überein'); return; }
    if (pw.new_password.length < 6) { toast.error('Mind. 6 Zeichen'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/auth/change-password`, { current_password: pw.current_password, new_password: pw.new_password });
      toast.success('Passwort geändert');
      setPw({ current_password: '', new_password: '', confirm: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-white mb-4">Passwort ändern</h2>
      <div>
        <Label className="text-slate-400 text-xs uppercase tracking-wider">Aktuelles Passwort</Label>
        <Input type="password" value={pw.current_password} onChange={e => setPw({...pw, current_password: e.target.value})}
          className="bg-slate-950 border-slate-800 text-slate-200 mt-1 focus:border-amber-500" />
      </div>
      <div>
        <Label className="text-slate-400 text-xs uppercase tracking-wider">Neues Passwort</Label>
        <Input type="password" value={pw.new_password} onChange={e => setPw({...pw, new_password: e.target.value})}
          placeholder="Mind. 6 Zeichen" className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1 focus:border-amber-500" />
      </div>
      <div>
        <Label className="text-slate-400 text-xs uppercase tracking-wider">Passwort bestätigen</Label>
        <Input type="password" value={pw.confirm} onChange={e => setPw({...pw, confirm: e.target.value})}
          className="bg-slate-950 border-slate-800 text-slate-200 mt-1 focus:border-amber-500" />
      </div>
      <Button onClick={handleChange} disabled={saving}
        className="bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold rounded-sm">
        {saving ? 'Speichern...' : 'Passwort ändern'}
      </Button>
    </div>
  );
}


function AttachmentManager({ API, isAdmin }) {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/attachments`).then(r => setAttachments(r.data)).catch(() => {});
  }, [API]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('name', file.name.replace(/\.[^.]+$/, ''));
    fd.append('attach_to', 'all');
    try {
      const res = await axios.post(`${API}/attachments`, fd);
      setAttachments(prev => [...prev, res.data]);
      toast.success('Anhang hochgeladen');
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); }
    finally { setLoading(false); e.target.value = ''; }
  };

  const handleToggle = async (att) => {
    try {
      const res = await axios.put(`${API}/attachments/${att.id}`, { active: !att.active });
      setAttachments(prev => prev.map(a => a.id === att.id ? res.data : a));
      toast.success(res.data.active ? 'Anhang aktiviert' : 'Anhang deaktiviert');
    } catch { toast.error('Fehler'); }
  };

  const handleAttachTo = async (att, value) => {
    try {
      const res = await axios.put(`${API}/attachments/${att.id}`, { attach_to: value });
      setAttachments(prev => prev.map(a => a.id === att.id ? res.data : a));
    } catch { toast.error('Fehler'); }
  };

  const handleDelete = async (id) => {
    await axios.delete(`${API}/attachments/${id}`);
    setAttachments(prev => prev.filter(a => a.id !== id));
    toast.success('Gelöscht');
  };

  const attachLabels = { all: 'Alle Kunden', gewerbe: 'Nur Gewerbekunden', privat: 'Nur Privatkunden', rechnung: 'Nur Rechnungen', angebot: 'Nur Angebote' };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-white flex items-center gap-2">
          <Paperclip className="w-4 h-4 text-amber-500" />
          Dokumenten-Anhänge
        </h3>
        {isAdmin && (
          <div>
            <Button variant="secondary" size="sm" onClick={() => fileRef.current?.click()} disabled={loading}
              className="bg-slate-800 text-slate-200 border border-slate-700 hover:bg-slate-700 rounded-sm">
              <Upload className="w-4 h-4 mr-1" />{loading ? 'Laden...' : 'Anhang hochladen'}
            </Button>
            <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.jpg,.png" className="hidden" onChange={handleUpload} />
          </div>
        )}
      </div>
      <p className="text-xs text-slate-500">Dokumente die automatisch beim E-Mail-Versand von Rechnungen/Angeboten beigefügt werden (z.B. AGB, Zertifikate, Leistungsverzeichnis).</p>

      {attachments.length > 0 ? (
        <div className="space-y-2">
          {attachments.map(att => (
            <div key={att.id} className="flex items-center justify-between py-3 px-4 bg-slate-950/50 rounded-sm border border-slate-800/40 group">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <Paperclip className="w-4 h-4 text-slate-400 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-300 truncate">{att.name}</p>
                  <p className="text-xs text-slate-500">{(att.size / 1024).toFixed(1)} KB · {att.filename}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <select value={att.attach_to || 'all'} onChange={e => handleAttachTo(att, e.target.value)} disabled={!isAdmin}
                  className="bg-slate-950 border border-slate-800 text-xs text-slate-300 rounded-sm px-2 py-1 disabled:opacity-50">
                  {Object.entries(attachLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <div className="flex items-center gap-1.5">
                  <Switch checked={att.active !== false} disabled={!isAdmin} onCheckedChange={() => handleToggle(att)}
                    className="data-[state=checked]:bg-emerald-500" />
                  <span className="text-[10px] text-slate-500 w-6">{att.active !== false ? 'An' : 'Aus'}</span>
                </div>
                <a href={`${API}/attachments/${att.id}/download`} target="_blank" rel="noreferrer">
                  <Button variant="ghost" size="icon" className="text-slate-400 hover:text-amber-400 w-7 h-7"><Download className="w-3.5 h-3.5" /></Button>
                </a>
                {isAdmin && (
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(att.id)} className="text-slate-400 hover:text-rose-400 w-7 h-7"><Trash2 className="w-3.5 h-3.5" /></Button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-slate-950/30 border border-dashed border-slate-700 rounded-sm p-6 text-center">
          <Paperclip className="w-8 h-8 text-slate-700 mx-auto mb-2" />
          <p className="text-sm text-slate-500">Keine Anhänge hinterlegt</p>
          <p className="text-xs text-slate-600 mt-1">Laden Sie Dokumente hoch die automatisch an E-Mails angehängt werden sollen</p>
        </div>
      )}
    </div>
  );
}


function NummernkreiseSection({ API, settings, update, isAdmin }) {
  const [nextNumbers, setNextNumbers] = useState({});

  useEffect(() => {
    axios.get(`${API}/settings/next-numbers`).then(r => setNextNumbers(r.data)).catch(() => {});
  }, [API]);

  const items = [
    { label: 'Rechnung', type: 'rechnung', prefixField: 'invoice_prefix', startField: 'invoice_number_start', defPrefix: 'RE' },
    { label: 'Angebot', type: 'angebot', prefixField: 'offer_prefix', startField: 'offer_number_start', defPrefix: 'ANG' },
    { label: 'Gutschrift', type: 'gutschrift', prefixField: 'credit_prefix', startField: 'credit_number_start', defPrefix: 'GS' },
    { label: 'Stornierung', type: 'stornierung', prefixField: 'cancel_prefix', startField: 'cancel_number_start', defPrefix: 'ST' },
    { label: 'Korrektur', type: 'rechnungskorrektur', prefixField: 'correction_prefix', startField: 'correction_number_start', defPrefix: 'RK' },
  ];

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-white mb-4">Nummernkreise</h2>
      <p className="text-sm text-slate-500">Jeder Dokumenttyp hat einen eigenen Nummernkreis mit separatem Präfix und Startnummer.</p>

      {items.map(item => {
        const nn = nextNumbers[item.type];
        return (
          <div key={item.prefixField} className="bg-slate-950/50 border border-slate-800/40 rounded-sm p-4">
            <div className="grid grid-cols-[1fr_120px_120px_200px] gap-4 items-end">
              <div>
                <Label className="text-slate-400 text-xs uppercase tracking-wider">{item.label}</Label>
                <div className="mt-1 text-xs text-slate-600">{nn ? `${nn.current_count} erstellt` : 'Dokumenttyp'}</div>
              </div>
              <div>
                <Label className="text-slate-400 text-xs uppercase tracking-wider">Präfix</Label>
                <Input value={settings[item.prefixField] || ''} onChange={e => update(item.prefixField, e.target.value)}
                  placeholder={item.defPrefix} disabled={!isAdmin}
                  className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1 disabled:opacity-50 font-mono" />
              </div>
              <div>
                <Label className="text-slate-400 text-xs uppercase tracking-wider">Start-Nr.</Label>
                <Input type="number" value={settings[item.startField] || ''} onChange={e => update(item.startField, parseInt(e.target.value) || 1)}
                  placeholder="1" disabled={!isAdmin}
                  className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1 disabled:opacity-50 font-mono" />
              </div>
              <div className="bg-slate-900 rounded-sm p-2.5 border border-slate-800 text-center">
                <span className="text-[10px] text-slate-500 block">Nächste Nr.</span>
                <span className="font-mono text-amber-400 text-sm">{nn?.next_number || '...'}</span>
              </div>
            </div>
          </div>
        );
      })}

      <p className="text-xs text-slate-500">Format: <span className="font-mono text-slate-400">[Präfix]-[Jahr]-[Laufende Nr.]</span> — Jeder Nummernkreis zählt unabhängig.</p>
    </div>
  );
}
